class LightSwiper {
  constructor(container, options = {}) {
    this.container = typeof container === 'string' ? document.querySelector(container) : container;
    if (!this.container) return;

    this.options = {
      loop: false,
      autoplay: false,
      speed: 300,
      slidesPerView: 1,
      spaceBetween: 0,
      navigation: {
        nextEl: null,
        prevEl: null
      },
      pagination: {
        el: null,
        clickable: true,
        type: 'bullets',
        renderBullet: null
      },
      breakpoints: {},
      ...options
    };

    this.init();
  }

  init() {
    this.wrapper = this.container.querySelector('.swiper-wrapper');
    this.slides = Array.from(this.container.querySelectorAll('.swiper-slide'));
    
    if (!this.wrapper || this.slides.length === 0) return;

    this.currentIndex = 0;
    this.isAnimating = false;
    this.autoplayTimer = null;
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.isDragging = false;

    // Mark this container as using LightSwiper to prevent conflicts
    this.container.setAttribute('data-light-swiper', 'true');
    
    this.setupContainer();
    this.setupNavigation();
    this.setupPagination();
    this.setupTouch();
    this.setupAutoplay();
    this.updateSlides();

    this.handleResize();
    window.addEventListener('resize', () => this.handleResize());
  }

  setupContainer() {
    this.container.style.overflow = 'hidden';
    this.wrapper.style.display = 'flex';
    this.wrapper.style.transition = `transform ${this.options.speed}ms ease`;
    this.wrapper.style.transform = 'translateX(0)';
  }

  setupNavigation() {
    const { nextEl, prevEl } = this.options.navigation;
    
    this.nextBtn = typeof nextEl === 'string' ? 
      this.container.querySelector(nextEl) : nextEl;
    this.prevBtn = typeof prevEl === 'string' ? 
      this.container.querySelector(prevEl) : prevEl;

    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.slideNext());
    }
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.slidePrev());
    }
  }

  setupPagination() {
    const { el, clickable, type, renderBullet } = this.options.pagination;
    if (!el) return;

    this.paginationEl = typeof el === 'string' ? 
      this.container.querySelector(el) : el;
    
    if (!this.paginationEl) return;

    if (type === 'bullets') {
      this.paginationEl.innerHTML = '';
      const slideCount = this.getEffectiveSlideCount();
      for (let i = 0; i < slideCount; i++) {
        const bullet = document.createElement('span');
        bullet.className = 'swiper-pagination-bullet';
        
        if (renderBullet) {
          bullet.innerHTML = renderBullet(i, 'swiper-pagination-bullet');
        }
        
        if (clickable) {
          bullet.addEventListener('click', () => this.slideTo(i));
        }
        
        this.paginationEl.appendChild(bullet);
      }
    } else if (type === 'fraction') {
      this.updateFractionPagination();
    }
  }

  getEffectiveSlideCount() {
    const slidesPerView = this.getCurrentSlidesPerView();
    return Math.max(1, this.slides.length - slidesPerView + 1);
  }

  setupTouch() {
    if (this.slides.length <= 1) return;
    
    this.container.addEventListener('touchstart', (e) => this.onTouchStart(e), { passive: true });
    this.container.addEventListener('touchmove', (e) => this.onTouchMove(e), { passive: false });
    this.container.addEventListener('touchend', (e) => this.onTouchEnd(e), { passive: true });
    
    this.container.addEventListener('mousedown', (e) => this.onTouchStart(e));
    this.container.addEventListener('mousemove', (e) => this.onTouchMove(e));
    this.container.addEventListener('mouseup', (e) => this.onTouchEnd(e));
    this.container.addEventListener('mouseleave', (e) => this.onTouchEnd(e));
  }

  setupAutoplay() {
    if (this.options.autoplay && this.options.autoplay.delay) {
      this.startAutoplay();
    }
  }

  onTouchStart(e) {
    // Don't allow touch interaction if there's only one slide
    if (this.slides.length <= 1) return;
    
    this.stopAutoplay();
    const touch = e.touches ? e.touches[0] : e;
    this.touchStartX = touch.clientX;
    this.touchStartY = touch.clientY;
    this.isDragging = true;
    this.wrapper.style.transition = 'none';
  }

  onTouchMove(e) {
    if (!this.isDragging || this.slides.length <= 1) return;
    
    const touch = e.touches ? e.touches[0] : e;
    const diffX = touch.clientX - this.touchStartX;
    const diffY = Math.abs(touch.clientY - this.touchStartY);
    
    if (Math.abs(diffX) > diffY) {
      e.preventDefault();
    }
    
    const slideWidth = this.getSlideWidth();
    const currentTranslate = -this.currentIndex * slideWidth;
    const newTranslate = currentTranslate + diffX;
    
    this.wrapper.style.transform = `translateX(${newTranslate}px)`;
  }

  onTouchEnd(e) {
    if (!this.isDragging || this.slides.length <= 1) return;
    
    this.isDragging = false;
    this.wrapper.style.transition = `transform ${this.options.speed}ms ease`;
    
    const touch = e.changedTouches ? e.changedTouches[0] : e;
    const diffX = touch.clientX - this.touchStartX;
    const threshold = this.getSlideWidth() * 0.2;
    
    if (Math.abs(diffX) > threshold) {
      if (diffX > 0) {
        this.slidePrev();
      } else {
        this.slideNext();
      }
    } else {
      this.updateSlides();
    }
    
    this.startAutoplay();
  }

  slideNext() {
    if (this.isAnimating) return;
    
    const slidesPerView = this.getCurrentSlidesPerView();
    const maxIndex = this.slides.length - slidesPerView;
    let nextIndex = this.currentIndex + 1;
    
    if (nextIndex > maxIndex) {
      if (this.options.loop) {
        nextIndex = 0;
      } else {
        return; // Don't slide if we're at the end and not looping
      }
    }
    
    this.slideTo(nextIndex);
  }

  slidePrev() {
    if (this.isAnimating) return;
    
    const slidesPerView = this.getCurrentSlidesPerView();
    const maxIndex = this.slides.length - slidesPerView;
    let prevIndex = this.currentIndex - 1;
    
    if (prevIndex < 0) {
      if (this.options.loop) {
        prevIndex = maxIndex;
      } else {
        return;
      }
    }
    
    this.slideTo(prevIndex);
  }

  slideTo(index) {
    if (this.isAnimating || index === this.currentIndex) return;
    
    const slidesPerView = this.getCurrentSlidesPerView();
    const maxIndex = this.slides.length - slidesPerView;
    
    // Clamp the index to valid range
    index = Math.max(0, Math.min(index, maxIndex));
    
    this.isAnimating = true;
    this.currentIndex = index;
    
    this.updateSlides();
    
    setTimeout(() => {
      this.isAnimating = false;
    }, this.options.speed);
  }

  updateSlides() {
    const slideWidth = this.getSlideWidth();
    const translateX = -this.currentIndex * slideWidth;
    
    this.wrapper.style.transform = `translateX(${translateX}px)`;
    
    this.slides.forEach((slide, index) => {
      slide.classList.remove('swiper-slide-active', 'swiper-slide-prev', 'swiper-slide-next');
      
      if (index === this.currentIndex) {
        slide.classList.add('swiper-slide-active');
      } else if (index === this.currentIndex - 1) {
        slide.classList.add('swiper-slide-prev');
      } else if (index === this.currentIndex + 1) {
        slide.classList.add('swiper-slide-next');
      }
    });
    
    this.updatePagination();
    this.updateNavigation();
  }

  updatePagination() {
    if (!this.paginationEl) return;
    
    if (this.options.pagination.type === 'bullets') {
      const bullets = this.paginationEl.querySelectorAll('.swiper-pagination-bullet');
      bullets.forEach((bullet, index) => {
        bullet.classList.toggle('swiper-pagination-bullet-active', index === this.currentIndex);
      });
    } else if (this.options.pagination.type === 'fraction') {
      this.updateFractionPagination();
    }
  }

  updateFractionPagination() {
    if (this.paginationEl) {
      const total = this.getEffectiveSlideCount();
      this.paginationEl.innerHTML = `${this.currentIndex + 1} / ${total}`;
    }
  }

  updateNavigation() {
    const slidesPerView = this.getCurrentSlidesPerView();
    const maxIndex = this.slides.length - slidesPerView;
    
    if (!this.options.loop) {
      if (this.prevBtn) {
        this.prevBtn.classList.toggle('swiper-button-disabled', this.currentIndex === 0);
      }
      if (this.nextBtn) {
        this.nextBtn.classList.toggle('swiper-button-disabled', this.currentIndex >= maxIndex);
      }
    } else {
      // Remove disabled class when looping
      if (this.prevBtn) {
        this.prevBtn.classList.remove('swiper-button-disabled');
      }
      if (this.nextBtn) {
        this.nextBtn.classList.remove('swiper-button-disabled');
      }
    }
  }

  getSlideWidth() {
    return this.container.offsetWidth / this.getCurrentSlidesPerView();
  }

  getCurrentSlidesPerView() {
    const windowWidth = window.innerWidth;
    let slidesPerView = this.options.slidesPerView;
    
    if (this.options.breakpoints) {
      Object.keys(this.options.breakpoints)
        .sort((a, b) => parseInt(a) - parseInt(b))
        .forEach(breakpoint => {
          if (windowWidth >= parseInt(breakpoint)) {
            slidesPerView = this.options.breakpoints[breakpoint].slidesPerView || slidesPerView;
          }
        });
    }
    
    return slidesPerView;
  }

  handleResize() {
    this.updateSlides();
    
    const slidesPerView = this.getCurrentSlidesPerView();
    const slideWidth = 100 / slidesPerView;
    
    this.slides.forEach(slide => {
      slide.style.flexShrink = '0';
      slide.style.width = `${slideWidth}%`;
    });

    const maxIndex = this.slides.length - slidesPerView;
    if (this.currentIndex > maxIndex) {
      this.currentIndex = maxIndex;
      this.updateSlides();
    }
  }

  startAutoplay() {
    if (!this.options.autoplay || !this.options.autoplay.delay) return;
    
    this.stopAutoplay();
    this.autoplayTimer = setInterval(() => {
      this.slideNext();
    }, this.options.autoplay.delay);
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  destroy() {
    this.stopAutoplay();
    this.container.style.overflow = '';
    this.wrapper.style.transform = '';
    this.wrapper.style.transition = '';
  }
}

window.Swiper = LightSwiper;

if (typeof module !== 'undefined' && module.exports) {
  module.exports = LightSwiper;
}

document.addEventListener('DOMContentLoaded', function() {
  const swiperContainers = document.querySelectorAll('.swiper');
  swiperContainers.forEach(container => {
    if (!container.hasAttribute('data-light-swiper')) {
      if (container.swiper && typeof container.swiper.destroy === 'function') {
        container.swiper.destroy();
      }
    }
  });
});